import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import PagePremission from '@/routes/Restrictions/PagePremission';
import { lazyRouteComponent, preloadComponent } from '@/utils/lazyLoad';
import CashCollectionPermission from '@/utils/premissions/CashCollectionPermission.enum';

// Lazy load the pages
const CashCollectionPage = lazyRouteComponent(
  () => import('@/pages/CashCollection'),
);

const EmployeeSafesPage = lazyRouteComponent(
  () => import('@/pages/EmployeeSafes'),
);

const MonthlyCashReportPage = lazyRouteComponent(
  () => import('@/pages/MonthlyCashReport'),
);

const EmployeeDailyTrackingPage = lazyRouteComponent(
  () => import('@/pages/EmployeeDailyTracking'),
);

const ChecksManagementPage = lazyRouteComponent(
  () => import('@/pages/ChecksManagement'),
);

// Preload the cash collection page since it's the default route
preloadComponent(() => import('@/pages/CashCollection'));

// Define routes for Treasury (الخزينة)
const TreasuryRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="/treasury/daily-collections" replace />,
  },
  {
    path: 'daily-collections',
    element: (
      <PagePremission prem={CashCollectionPermission.Prem_CashCollection_Add}>
        <CashCollectionPage />
      </PagePremission>
    ),
  },
  {
    path: 'employee-safes',
    element: (
      <PagePremission prem={CashCollectionPermission.Prem_CashCollection_Manage}>
        <EmployeeSafesPage />
      </PagePremission>
    ),
  },
  {
    path: 'monthly-report',
    element: (
      <PagePremission prem={CashCollectionPermission.Prem_CashCollection_Manage}>
        <MonthlyCashReportPage />
      </PagePremission>
    ),
  },
  {
    path: 'employee-tracking',
    element: (
      <PagePremission prem={CashCollectionPermission.Prem_CashCollection_Manage}>
        <EmployeeDailyTrackingPage />
      </PagePremission>
    ),
  },
  {
    path: 'checks',
    element: (
      <PagePremission prem={CashCollectionPermission.Prem_CashCollection_Manage}>
        <ChecksManagementPage />
      </PagePremission>
    ),
  },
];

// Advanced preloading API for context-aware prefetching
export const prefetchTreasuryRoutes = {
  prefetchDailyCollections: () => void import('@/pages/CashCollection'),
  prefetchEmployeeSafes: () => void import('@/pages/EmployeeSafes'),
  prefetchMonthlyCashReport: () => void import('@/pages/MonthlyCashReport'),
  prefetchEmployeeDailyTracking: () =>
    void import('@/pages/EmployeeDailyTracking'),
  prefetchChecksManagement: () => void import('@/pages/ChecksManagement'),
  prefetchAllTreasury: () => {
    void import('@/pages/CashCollection');
    void import('@/pages/EmployeeSafes');
    void import('@/pages/MonthlyCashReport');
    void import('@/pages/EmployeeDailyTracking');
    void import('@/pages/ChecksManagement');
  },
};

export default TreasuryRoutes;
